import { linkTo } from "../api/links";
import type { Feed } from "../api/types";
import { Badge, Empty, Guid, SEQ, Time } from "./primitives";

type Column = "load" | "driver" | "carrier";

/**
 * `current` is the connection the load is assigned to now. On a load's own screen the feed
 * holds a row per driver who ever had it, so the others are marked rather than hidden: a
 * reassignment shows up as the old driver's `delete`.
 */
export function FeedTable({
  feed,
  omit = [],
  current,
  empty,
}: {
  feed: Feed[];
  omit?: Column[];
  current?: { driver_guid: string | null; carrier_guid: string | null };
  empty: React.ReactNode;
}) {
  if (feed.length === 0) return <Empty>{empty}</Empty>;

  const show = (column: Column) => !omit.includes(column);
  const template = [
    "minmax(64px,auto)",
    show("load") && "minmax(300px,auto)",
    show("driver") && "minmax(300px,auto)",
    show("carrier") && "minmax(300px,auto)",
    "64px",
    "minmax(110px,1fr)",
  ]
    .filter(Boolean)
    .join(" ");
  const grid = "grid items-center gap-x-4 px-4 py-2";

  return (
    <div className="overflow-x-auto">
      <div
        className={`min-w-max border-b border-line ${grid}`}
        style={{ gridTemplateColumns: template }}
      >
        <span className="label">action</span>
        {show("load") && <span className="label">load</span>}
        {show("driver") && <span className="label">driver</span>}
        {show("carrier") && <span className="label">carrier</span>}
        <span className="label" title={SEQ.rootCursor}>
          cursor
        </span>
        <span className="label text-right">updated</span>
      </div>
      {feed.map((entry) => {
        const previous =
          current &&
          (entry.driver_guid !== current.driver_guid ||
            entry.carrier_guid !== current.carrier_guid);
        return (
          <div
            key={entry.id}
            className={`row-hover min-w-max border-b border-line/70 last:border-b-0 ${grid}`}
            style={{ gridTemplateColumns: template }}
          >
            <span className="flex items-center gap-2">
              {entry.action === "delete" ? (
                <Badge tone="bad">delete</Badge>
              ) : (
                <span className="text-xs text-muted">{entry.action}</span>
              )}
              {previous && <Badge tone="muted">not assigned now</Badge>}
            </span>
            {show("load") && (
              <Guid value={entry.root_guid} to={linkTo.root(entry.root_type, entry.root_guid)} />
            )}
            {show("driver") && (
              <Guid value={entry.driver_guid} to={linkTo.driver(entry.driver_guid)} />
            )}
            {show("carrier") && (
              <Guid value={entry.carrier_guid} to={linkTo.carrier(entry.carrier_guid)} />
            )}
            <span className="mono text-xs text-body" title={SEQ.rootCursor}>
              {entry.cursor}
            </span>
            <span className="text-right">
              <Time value={entry.updated_at} />
            </span>
          </div>
        );
      })}
    </div>
  );
}
